import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { HttpErrorHandlerService, HandleError } from '../shared/http-error-handler.service';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ForuscareService } from '../foruscare/foruscare.service';
import { SharedService } from '../shared/shared.service';

import { Range } from 'ngx-mat-daterange-picker';

@Injectable({
    providedIn: 'root'
})
export class DashboardService {
    private handleError: HandleError;

    constructor(
        private http: HttpClient,
        private fourscareService: ForuscareService,
        private sharedService: SharedService,
        httpErrorHandler: HttpErrorHandlerService
    ) {
        this.handleError = httpErrorHandler.createHandleError('Dashboard Service');
    }

    private getLocalDate(date: Date): string {
        const local = new Date(date);
        local.setMinutes(date.getMinutes() - date.getTimezoneOffset());

        return local.toJSON().slice(0, 10);
    }

    getOverallDetails(): Observable<any> {
        const currentDate = this.getLocalDate(new Date());
        const url = `${this.fourscareService.baseAdminURL()}/dashboard/allCount/${currentDate}`;

        return this.http.get<any>(url).pipe( catchError( this.handleError('Dashboard: overall details') ));
    }

    getServicesTakenDetails(selectedDateRange: Range, accountId: any|number = null, orgId: any|number = null): Observable<any> {
        const {fromDate, toDate} = selectedDateRange;
        const fromDateString = this.getLocalDate(fromDate);
        const toDateString = this.getLocalDate(toDate);
        const chartType = 'fundusSplitbyDate';
        const url = `${this.fourscareService.baseAdminURL()}/dashboard/${chartType}/${fromDateString}/${toDateString}/${orgId}/${accountId}`;

        return this.http.get<any>(url).pipe( catchError( this.handleError('Dashboard: Services Taken details') ));
    }

    getDiseaseFoundDetails(selectedDateRange: Range, accountId: any|number = null, orgId: any|number = null, type: string): Observable<any> {
        const {fromDate, toDate} = selectedDateRange;
        const fromDateString = this.getLocalDate(fromDate);
        const toDateString = this.getLocalDate(toDate);
        const chartType = (type === 'pie') ? 'diseaseCount' : 'diseaseSplitByDate';
        const url = `${this.fourscareService.baseAdminURL()}/dashboard/${chartType}/${fromDateString}/${toDateString}/${orgId}/${accountId}`;

        return this.http.get<any>(url).pipe( catchError( this.handleError('Dashboard: Disease Found details') ));
    }

    getPackageSubscriptionHistoryDetails(selectedDateRange: Range, accountId: any|number = null, orgId: any|number = null): Observable<any> {
        const {fromDate, toDate} = selectedDateRange;
        const fromDateString = this.getLocalDate(fromDate);
        const toDateString = this.getLocalDate(toDate);
        const chartType = 'paymentSplitByDate';
        const url = `${this.fourscareService.baseAdminURL()}/dashboard/${chartType}/${fromDateString}/${toDateString}/${orgId}/${accountId}`;

        return this.http.get<any>(url).pipe( catchError( this.handleError('Dashboard: Package Subscription History details') ));
    }

    getPaymentHistoryDetails(selectedDateRange: Range, accountId: any|number = null, orgId: any|number = null): Observable<any> {
        const {fromDate, toDate} = selectedDateRange;
        const fromDateString = this.getLocalDate(fromDate);
        const toDateString = this.getLocalDate(toDate);
        const url = `${this.fourscareService.baseAdminURL()}/dashboard/paymentCount/${fromDateString}/${toDateString}/${orgId}/${accountId}`;

        return this.http.get<any>(url).pipe( catchError( this.handleError('Dashboard: Payment History details') ));
    }

    getPatientsSexDetails(selectedDateRange: Range, accountId: any|number = null, orgId: any|number = null): Observable<any> {
        const {fromDate, toDate} = selectedDateRange;
        const fromDateString = this.getLocalDate(fromDate);
        const toDateString = this.getLocalDate(toDate);
        const url = `${this.fourscareService.baseAdminURL()}/dashboard/patientGenderData/${fromDateString}/${toDateString}/${orgId}/${accountId}`;
        return this.http.get<any>(url).pipe( catchError( this.handleError('Dashboard: overall details') ));
    }

    getPatientsAgeDetails(selectedDateRange: Range, accountId: any|number = null, orgId: any|number = null, type: string): Observable<any> {
        const {fromDate, toDate} = selectedDateRange;
        const fromDateString = this.getLocalDate(fromDate);
        const toDateString = this.getLocalDate(toDate);
        const chartType = (type === 'pie') ? 'patientAgeSplit' : 'patientAgeSplitByDate';
        const url = `${this.fourscareService.baseAdminURL()}/dashboard/${chartType}/${fromDateString}/${toDateString}/${orgId}/${accountId}`;

        return this.http.get<any>(url).pipe( catchError( this.handleError('Dashboard: Patient sAge details') ));
    }

    getFundusStatusDetails(selectedDateRange: Range, accountId: any|number = null, orgId: any|number = null, type: string): Observable<any> {
        const {fromDate, toDate} = selectedDateRange;
        const fromDateString = this.getLocalDate(fromDate);
        const toDateString = this.getLocalDate(toDate);
        const chartType = (type === 'pie') ? 'fundusCount' : 'fundusStatusSplitByDate';
        const url = `${this.fourscareService.baseAdminURL()}/dashboard/${chartType}/${fromDateString}/${toDateString}/${orgId}/${accountId}`;

        return this.http.get<any>(url).pipe( catchError( this.handleError('Dashboard: Fundus Status details') ));
    }

    getFundusDeviceDetails(selectedDateRange: Range, accountId: any|number = null, orgId: any|number = null, type: string): Observable<any> {
        const {fromDate, toDate} = selectedDateRange;
        const fromDateString = this.getLocalDate(fromDate);
        const toDateString = this.getLocalDate(toDate);
        const chartType = (type === 'pie') ? 'deviceCount' : 'deviceSplitByDate';
        const url = `${this.fourscareService.baseAdminURL()}/dashboard/${chartType}/${fromDateString}/${toDateString}/${orgId}/${accountId}`;

        return this.http.get<any>(url).pipe( catchError( this.handleError('Dashboard: Fundus Device details') ));
    }

    getAIGradeabilityDetails(selectedDateRange: Range, accountId: any|number = null, orgId: any|number = null, type: string): Observable<any> {
        const {fromDate, toDate} = selectedDateRange;
        const fromDateString = this.getLocalDate(fromDate);
        const toDateString = this.getLocalDate(toDate);
        const chartType = (type === 'pie') ? 'gradeCount' : 'gradeSplitByDate';
        const url = `${this.fourscareService.baseAdminURL()}/dashboard/${chartType}/${fromDateString}/${toDateString}/${orgId}/${accountId}`;

        return this.http.get<any>(url).pipe( catchError( this.handleError('Dashboard: AI Gradeability details') ));
    }

    getAIDiagnosisDetails(selectedDateRange: Range, accountId: any|number = null, orgId: any|number = null, type: string): Observable<any> {
        const {fromDate, toDate} = selectedDateRange;
        const fromDateString = this.getLocalDate(fromDate);
        const toDateString = this.getLocalDate(toDate);
        const chartType = (type === 'pie') ? 'drdmeCount' : 'drdmeSplitByDate';
        const url = `${this.fourscareService.baseAdminURL()}/dashboard/${chartType}/${fromDateString}/${toDateString}/${orgId}/${accountId}`;

        return this.http.get<any>(url).pipe( catchError( this.handleError('Dashboard: AI Diagnosis details') ));
    }
}
